import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { Button } from '@connected/ui/components/button';
import { Popout } from '@connected/ui/components/popout';
import { useForm } from '@tanstack/react-form';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormTouched } from '@/hooks/use-form-touched';
import { showConfirm } from '@/stores/confirmer';

import type { TechnicalDrawing, TechnicalDrawingFormFields } from '../utils/drawing-types';

import { useUpdateDrawing } from '../hooks/use-drawing-mutations';
import { drawingDefaultValues } from '../utils/drawing-defaults';
import { DrawingFormFields } from './drawing-form-fields';

export type DrawingEditModalProps = {
    technicalDrawing: TechnicalDrawing;
    electricalProjectVersionId: string;
};

export function DrawingEditModal({ technicalDrawing, electricalProjectVersionId }: DrawingEditModalProps) {
    const { t } = useTranslation('applications');
    const [open, setOpen] = useState(false);

    const { mutation: updateDrawingMutation, disabled } = useUpdateDrawing({ electricalProjectVersionId });

    const form = useForm<TechnicalDrawingFormFields, ZodValidator>({
        defaultValues: {
            ...drawingDefaultValues(),
            ...technicalDrawing,
        },
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            const body = {
                ElectricalProjectTechnicalDrawingId: technicalDrawing.ElectricalProjectTechnicalDrawingId,
                ElectricalProjectFileId: value.ElectricalProjectFileId ?? null,
                Name: value.Name ?? null!,
                Description: value.Description ?? null,
                Location: value.Location ?? null,
                IsDrawingPresent: value.IsDrawingPresent ?? null,
                Order: 999,
            };

            await updateDrawingMutation.mutateAsync({ body });
            form.reset();
            setOpen(false);
        },
    });

    const isTouched = useFormTouched(form.store, drawingDefaultValues);

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        event.stopPropagation();
        form.handleSubmit();
    }

    async function handleOpenChange(open: boolean) {
        if (open) {
            setOpen(true);
            return;
        }
        else if (!isTouched) {
            setOpen(false);
            return;
        }

        if (await showConfirm(t('messages.general.confirm_cancel', { ns: 'common' }))) {
            form.reset();
            setOpen(false);
        }
    }

    return (
        <Popout open={open} onOpenChange={handleOpenChange}>
            <Popout.Trigger asChild>
                <Button className="bg-background/80 hover:bg-background" variant="ghost" size="icon" disabled={disabled}>
                    <Pencil />
                </Button>
            </Popout.Trigger>
            <Popout.Content>
                <Popout.Header>
                    <Popout.Title>Tekening bijwerken</Popout.Title>
                    <Popout.Description className="sr-only">
                        Een korte omschrijving van de tekening en eventuele bijzonderheden.
                    </Popout.Description>
                </Popout.Header>
                <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                    <Popout.Body>
                        <DrawingFormFields
                            form={form}
                            disabled={disabled}
                            electricalProjectVersionId={electricalProjectVersionId}
                        />
                    </Popout.Body>
                    <Popout.Footer>
                        <Popout.Close asChild>
                            <Button variant="outline" className="w-full" disabled={disabled}>
                                {t('actions.general.cancel', { ns: 'common' })}
                            </Button>
                        </Popout.Close>
                        <Button type="submit" className="w-full" disabled={disabled}>
                            {t('actions.general.save', { ns: 'common' })}
                        </Button>
                    </Popout.Footer>
                </form>
            </Popout.Content>
        </Popout>
    );
}
